import { useCallback, useEffect, useMemo } from 'react'

import sanitizeError from '~/utils/sanitizeError'
import { DEFAULT_ITEM_STATE } from '~/utils/slices/constants'
import { useListSliceKey } from '~/utils/slices/listSliceKey'

import type { ChatThread, ChatThreadsPayload } from '../@types'
import { fetchChatThreads } from '../api'
import { DEFAULT_SERIALIZER } from '../constants'
import useChatThreadEntitiesSlice from '../slices/useChatThreadEntitiesSlice'
import useChatThreadListsSlice from '../slices/useChatThreadListsSlice'

type Pagination = ChatThreadsPayload['pagination']
type Sort = ChatThreadsPayload['sort']

const useChatThreads = (inPagination?: Pagination, inSort?: Sort) => {
  const listSliceKey = useListSliceKey(undefined, inPagination, inSort, DEFAULT_SERIALIZER)

  const { setItemLoadSuccess: setChatThreadEntityLoadSuccess, items: chatThreadEntities } =
    useChatThreadEntitiesSlice()

  const { loading, loaded, error, items, meta } = useChatThreadListsSlice(
    slice => slice.items[listSliceKey] || DEFAULT_ITEM_STATE,
  )
  const { setItemLoadStart, setItemLoadSuccess, setItemLoadFailure, getItem } =
    useChatThreadListsSlice()

  const fetch = async (
    newKey: string,
    newPagination: ChatThreadsPayload['pagination'],
    newSort: ChatThreadsPayload['sort'],
  ) => {
    const state = getItem(newKey)
    if (state?.loading) {
      return
    }
    try {
      setItemLoadStart(newKey, undefined, newPagination, newSort, null)
      const { data, meta } = await fetchChatThreads({
        filters: undefined,
        pagination: newPagination,
        sort: newSort,
        serializer: DEFAULT_SERIALIZER,
      })
      const items = data.map(item => {
        setChatThreadEntityLoadSuccess(item.id, item)
        return item.id
      })

      return { data, meta, items }
    } catch (e) {
      setItemLoadFailure(newKey, sanitizeError(e))
    }
  }

  const load = useCallback(async () => {
    const state = getItem(listSliceKey)
    if (state?.error || state?.loaded) return
    const results = await fetch(listSliceKey, inPagination, inSort)
    if (results) {
      setItemLoadSuccess(listSliceKey, results.items, results.meta)
    }
  }, [listSliceKey])

  const reload = useCallback(async () => {
    const results = await fetch(listSliceKey, inPagination, inSort)
    if (results) {
      setItemLoadSuccess(listSliceKey, results.items, results.meta)
    }
  }, [listSliceKey])

  const hasMore = useMemo(() => {
    return (items.length || 0) < (meta?.page.total || 0)
  }, [items, meta])

  const loadMore = async () => {
    const state = getItem(listSliceKey)
    if (!hasMore || !state?.pagination) return

    const results = await fetch(
      listSliceKey,
      {
        ...state?.pagination,
        offset: state?.pagination?.offset + 1,
      },
      state?.sort,
    )

    if (results) {
      setItemLoadSuccess(listSliceKey, state.items.concat(results.items), results.meta)
    }
  }

  useEffect(() => {
    load()
  }, [listSliceKey])

  const parsedItems = useMemo(() => {
    return items.reduce<ChatThread[]>((collector, id) => {
      const threadState = chatThreadEntities[id]
      if (threadState?.loaded) {
        collector.push(threadState.data)
      }
      return collector
    }, [])
  }, [items, chatThreadEntities])

  return {
    items: parsedItems,
    meta,
    loading,
    loaded,
    error,
    hasMore,
    loadMore,
    reload,
  }
}

export default useChatThreads
